'use client';

import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import 'swiper/css';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import { trackEvent } from '@/lib/amplitude';
import PrimaryButton from '@/components/shared/PrimaryButton';
import { formatCountdown, msLeft, type PendingApprovalBook } from '@/lib/comix/pending-approvals';

/** 1s clock, scoped to the card so only the countdown text re-renders each tick —
 *  never the framer-animated popup wrapper. */
function useSecondsTick(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

// The homepage's own `dt:` breakpoint (800px, see tailwind.config.js) is what
// everything else on this page flips to desktop at, so this uses it too instead of
// Tailwind's stock `sm:` (540px), which used to switch this card to a desktop row
// while the rest of the homepage (`.hp`, homepage.css) was still rendering its
// 501-799px "mobile, just centered" range.
// 'popup' covers every non-homepage page it's mounted on now (see
// PendingApprovalPopup.tsx) — it used to be 'select-popup' | 'stories-popup' when it
// only lived on those two pages; the actual page is still tracked, just via
// PendingApprovalPopup's own trackEvent payload (a `page` field) rather than this.
type Source = 'home-banner' | 'popup';

function Countdown({ deadlineAt }: { deadlineAt: string }) {
  const now = useSecondsTick();
  return (
    // bg-[#FFF0F6] (not white) — this now sits inside the top-right corner of the
    // white card itself, so it needs its own contrast instead of relying on the
    // tint behind it.
    <span
      className="rounded-full bg-[#FFF0F6] px-2.5 py-1 font-mono text-[12px] font-semibold tabular-nums text-[#F52F81] shadow-sm"
      aria-label="Time left to approve"
    >
      {formatCountdown(msLeft(deadlineAt, now))}
    </span>
  );
}

/** Text + cover for one book. Renders no background of its own — either the
 *  banner's white card or the popup's white panel (see the default export below and
 *  PendingApprovalPopup.tsx) supplies whatever surface this sits on.
 *
 *  Desktop cover height, padding and headline size all branch on `isCompact` — the
 *  popup is a much narrower panel (~720px, see PendingApprovalPopup.tsx) than the
 *  home banner's full 1040px hp-container, and the banner's own numbers (dt:h-[400px]
 *  cover, dt:p-[5.5rem] padding, dt:text-[40px] headline) overflowed the popup badly
 *  before this branched — the cover was literally bleeding past the panel's edge. */
function BookContent({ book, source }: { book: PendingApprovalBook; source: Source }) {
  const isCompact = source !== 'home-banner';
  return (
    <div className={`flex flex-col-reverse items-center gap-5 p-6 text-center dt:h-full dt:flex-row dt:items-center dt:gap-0 dt:text-left ${isCompact ? 'dt:p-10' : 'dt:p-[5.5rem]'}`}>
      <div className="flex flex-col items-center dt:flex-1 dt:items-start">
        {/* Forced break, at a different word per breakpoint — always exactly this
            way, never wherever the column happens to wrap on its own:
            mobile:  "Approve your comic to" / "start shipping"
            desktop: "Approve your comic" / "to start shipping" */}
        <h3 className={`text-[26px] font-bold leading-tight text-[#111] ${isCompact ? 'dt:text-[28px]' : 'dt:text-[40px]'}`}>
          {book.printed ? (
            <>Approve your comic<br className="hidden dt:block" /> to<br className="dt:hidden" /> start shipping</>
          ) : (
            <>Approve your comic<br className="hidden dt:block" /> to<br className="dt:hidden" /> get your book</>
          )}
        </h3>
        <PrimaryButton
          id="btn-approval-cta"
          href={`/comix/approve?runId=${book.runId}`}
          onClick={() => trackEvent('Home - Approval Banner CTA Clicked', { source, runId: book.runId })}
          className="mt-5"
        >
          View &amp; Approve
        </PrimaryButton>
      </div>

      {/* Square corners on purpose — this is a product photo sitting flush in the
          card, not a rounded thumbnail (see the GlassesUSA reference). Mobile:
          literal w-[240px] (height follows from the aspect-ratio class, not
          hardcoded) regardless of context — the popup's mobile view is full-width
          like any other mobile page, no compact variant needed there. aspect-[2/3]
          is just the BOX's shape (needed so it has a real width/height to size
          against — an aspect-ratio-less box with only a height, no width, and no
          content collapses to 0 width, the same bug documented elsewhere in this
          file); object-contain on the image itself means an actual cover whose real
          proportions differ a bit from 2:3 still shows in full, uncropped, letterboxed
          on gray-100 rather than losing its top/bottom to object-cover. No
          overflow-hidden here — this way the shadow isn't clipped by it. */}
      <div className={`relative aspect-[2/3] w-[240px] max-w-full flex-none bg-gray-100 shadow-lg dt:w-auto dt:max-w-none ${isCompact ? 'dt:h-[260px]' : 'dt:h-[400px]'}`}>
        <Image
          src={book.coverImage}
          alt={book.storyHeadline}
          fill
          sizes="(max-width: 799px) 240px, 260px"
          className="object-contain"
        />
      </div>
    </div>
  );
}

// Same size/colors/hover treatment as ReviewsSlider.tsx's edge arrows ("Join our
// community"), but two deliberate differences: shown on mobile too (that slider
// hides them below dt: and relies on swipe alone — this one keeps them visible at
// every breakpoint), and positioned to sit half on the card, half on the tint behind
// it — `-translate-x-1/2` (combined with `-translate-y-1/2` per-button below) shifts
// the button by its own half-width past `left-0`/`right-0`, so it straddles the
// card's edge instead of sitting inset within it.
const ARROW_CLS =
  'flex absolute top-1/2 z-10 h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white text-[#333333] shadow-md transition-all duration-200 hover:scale-95 hover:bg-[#FFF0F6] hover:text-[#F52F81] hover:shadow-lg active:scale-90 active:duration-100';

/** The content used by both the home banner and the select/stories popup — the
 *  caller supplies the tinted backdrop (a <section> for the banner, a panel for the
 *  popup) and this renders the actual white card on top of it. One book → the plain
 *  layout. More than one → a swipeable carousel with the same edge arrows the
 *  home-page review slider uses; the cover + CTA + countdown all follow the active
 *  slide. */
export default function PendingApprovalCard({
  books,
  source,
}: {
  books: PendingApprovalBook[];
  source: Source;
}) {
  const swiperRef = useRef<SwiperType | null>(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const update = (s: SwiperType) => {
    setIsBeginning(s.isBeginning);
    setIsEnd(s.isEnd);
    setActiveIndex(s.activeIndex);
  };

  if (books.length === 0) return null;

  const activeBook = books[activeIndex] ?? books[0];
  // isCompact === it's the popup, not the home banner. The popup's own panel
  // (PendingApprovalPopup.tsx) is already a plain white surface, so this skips its
  // OWN elevated white/shadowed card shell there — no card floating on a card. The
  // banner still gets the real card (a white surface sitting on the tinted
  // section). Both are left auto-height: nothing inside depends on a percentage
  // height anymore, everything's a literal pixel size.
  const isCompact = source !== 'home-banner';

  return (
    // Outer wrapper has NO overflow-hidden — the arrows below float half outside the
    // edge, so clipping at this level would cut them in half.
    <div className="relative">
      {/* relative always (the countdown badge below anchors to this); the
          white/rounded/shadowed "card" look is the banner only. */}
      <div className={`relative ${isCompact ? '' : 'overflow-hidden rounded-[16px] bg-white shadow-[0_4px_24px_rgba(0,0,0,0.08)]'}`}>
        {/* Countdown — top-right corner, inside the card. Tracks whichever book is
            currently showing. */}
        <div className="absolute right-3 top-3 z-10 dt:right-5 dt:top-5">
          <Countdown deadlineAt={activeBook.deadlineAt} />
        </div>

        <div className="dt:h-full">
          {books.length === 1 ? (
            <BookContent book={books[0]} source={source} />
          ) : (
            <Swiper
              onSwiper={(s) => { swiperRef.current = s; update(s); }}
              onSlideChange={update}
              slidesPerView={1}
              spaceBetween={16}
              className="dt:h-full"
            >
              {books.map((book) => (
                <SwiperSlide key={book.runId}>
                  <BookContent book={book} source={source} />
                </SwiperSlide>
              ))}
            </Swiper>
          )}
        </div>
      </div>

      {/* Arrows sit on the OUTER wrapper, not inside the card — `-translate-x-1/2`
          centers each button on the card's left/right edge, so exactly half of it
          floats over the card and half over the tint behind it. */}
      {books.length > 1 && (
        <>
          <button
            type="button"
            aria-label="Previous book"
            onClick={() => { swiperRef.current?.slidePrev(); trackEvent('Home - Approval Banner Carousel Nav', { source, dir: 'prev' }); }}
            className={`${ARROW_CLS} left-0 -translate-x-1/2 ${isBeginning ? 'pointer-events-none opacity-0' : 'opacity-100'}`}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
          </button>
          <button
            type="button"
            aria-label="Next book"
            onClick={() => { swiperRef.current?.slideNext(); trackEvent('Home - Approval Banner Carousel Nav', { source, dir: 'next' }); }}
            className={`${ARROW_CLS} right-0 translate-x-1/2 ${isEnd ? 'pointer-events-none opacity-0' : 'opacity-100'}`}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
          </button>
        </>
      )}
    </div>
  );
}
